import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { BetaBadge } from "@/components/BetaBadge";
import { LanguageSelector } from "@/components/LanguageSelector";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationCenter } from "@/components/NotificationCenter";
import { CATEGORIES } from "@/lib/api";
import { useI18n } from "@/i18n";

export function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, catName } = useI18n();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="fixed top-9 left-0 right-0 z-50 backdrop-blur-md bg-white/85 border-b border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 shrink-0">
            <div data-testid="nav-logo-next-intelligence">
              <Logo subtitle />
            </div>
            <BetaBadge />
            <span data-testid="live-indicator" className="hidden sm:inline-flex items-center gap-1.5 ps-2.5 pe-3 py-1 rounded-full bg-violet-50 border border-violet-100">
              <span className="live-ring w-1.5 h-1.5 rounded-full bg-violet-600" />
              <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-violet-700">Live</span>
            </span>
          </div>

          <form onSubmit={submit} className="hidden md:flex flex-1 max-w-xs ms-auto relative">
            <Search size={16} className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="nav-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("common.searchPlaceholder")}
              className="w-full ps-9 pe-3 py-2 text-sm bg-slate-100 rounded-full border border-transparent focus:border-violet-300 focus:bg-white outline-none transition-colors"
            />
          </form>

          <div className="hidden md:flex items-center gap-2">
            <LanguageSelector />
            <ThemeToggle />
          </div>
          <div className="ms-auto md:ms-0">
            <NotificationCenter />
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 -me-2"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        <nav className="hidden md:flex items-center gap-1 overflow-x-auto no-scrollbar pb-2 -mt-1">
          <Link to="/x-pulse" data-testid="nav-link-x-pulse" className="whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold text-white bg-black rounded-full hover:bg-slate-800 transition-colors ring-1 ring-violet-500/40"><span className="font-black">𝕏</span> Pulse</Link>
          {CATEGORIES.map((c) => (
            <Link
              key={c.slug}
              to={c.slug === "latest" ? "/latest" : `/category/${c.slug}`}
              data-testid={`nav-link-${c.slug}`}
              className="whitespace-nowrap px-3 py-1.5 text-[13px] font-medium text-slate-600 hover:text-violet-700 rounded-full hover:bg-violet-50 transition-colors"
            >
              {catName(c.slug)}
            </Link>
          ))}
        </nav>
      </div>

      {open && (
        <div className="md:hidden slide-down border-t border-slate-200 bg-white px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <LanguageSelector />
            <ThemeToggle />
          </div>
          <form onSubmit={submit} className="relative mb-3">
            <Search size={16} className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="mobile-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("common.searchPlaceholder")}
              className="w-full ps-9 pe-3 py-2.5 text-sm bg-slate-100 rounded-full outline-none"
            />
          </form>
          <Link to="/x-pulse" onClick={() => setOpen(false)} data-testid="mobile-nav-x-pulse" className="mb-2 flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-white bg-black rounded-lg"><span className="font-black">𝕏</span> Pulse — AI & Tech</Link>
          <div className="grid grid-cols-2 gap-1">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={c.slug === "latest" ? "/latest" : `/category/${c.slug}`}
                className="px-3 py-2.5 text-sm font-medium text-slate-700 rounded-lg hover:bg-violet-50"
              >
                {catName(c.slug)}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
